import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { 
  Plus, CheckCircle2, Settings, Ban, Edit2, Pause, Play, RotateCcw, 
  Target, X, ChevronDown, FolderOpen,
  StopCircle, ArrowRight, Users, Trophy, Clock,
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { StreakWidget } from '../components/StreakWidget';
import { GoalCelebration } from '../components/GoalCelebration';

// ============================================
// MONK LEVEL HERO CARD - PREMIUM VISUAL
// ============================================
function SharedGoalCard({ goal, onLog, onNavigate, isDark }) {
  const [logging, setLogging] = useState(false);
  const progressPercent = Math.round((goal.user_progress / goal.target_value) * 100);
  const isCompleted = goal.user_progress >= goal.target_value;
  
  const handleLog = async (e) => {
    e.stopPropagation();
    if (isCompleted || goal.status !== 'ACTIVE') return;
    
    setLogging(true);
    try {
      await onLog();
    } finally {
      setLogging(false);
    }
  };
  
  return (
    <div 
      className={cn(
        "flex-shrink-0 p-4 rounded-xl transition-all min-w-[220px] group",
        isDark 
          ? "bg-zinc-800/50 border border-white/5 hover:bg-zinc-700/50 hover:border-violet-500/30" 
          : "bg-white shadow-sm border border-violet-50 hover:shadow-md hover:border-[#E5E2DA]"
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <p 
          className={cn(
            "text-sm font-medium truncate flex-1 cursor-pointer transition-colors",
            isDark ? "text-white hover:text-violet-400" : "text-[#1A1A1A] hover:text-[#1A1A1A]"
          )}
          onClick={onNavigate}
        >
          {goal.title}
        </p>
        <div className={cn("flex items-center gap-1 text-xs", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
          <Users className="w-3 h-3" />
          <span>{goal.participant_count}</span>
        </div>
      </div>
      
      <div className="flex items-center gap-2 mb-3">
        <Progress value={progressPercent} className="flex-1 h-2" />
        <span className={cn("text-xs font-mono", isDark ? "text-zinc-400" : "text-[#9E8778]")}>{progressPercent}%</span>
      </div>
      
      <div className="flex items-center justify-between">
        <span className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
          {goal.user_progress}/{goal.target_value} {goal.target_unit}
        </span>
        
        {goal.status === 'ACTIVE' && !isCompleted ? (
          <Button
            size="sm"
            onClick={handleLog}
            disabled={logging}
            className="h-7 px-3 text-xs font-medium bg-[#F4F3EE]0/20 text-[#1A1A1A] hover:bg-[#F4F3EE]0/30 border border-violet-500/30"
            data-testid={`shared-goal-log-${goal.id}`}
          >
            {logging ? (
              <span className="animate-pulse">...</span>
            ) : (
              <><Plus className="w-3 h-3 mr-1" /> Log</>
            )}
          </Button>
        ) : isCompleted ? (
          <span className="flex items-center gap-1 text-xs text-emerald-500">
            <CheckCircle2 className="w-3.5 h-3.5" /> Done
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================
function EmptyState({ onCreateGoal, isDark }) {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center py-20 px-8 backdrop-blur-xl rounded-2xl",
      isDark 
        ? "bg-zinc-900/40 border border-white/[0.06]" 
        : "bg-white shadow-lg border border-violet-50"
    )}>
      <div className="relative mb-6">
        <div className={cn("absolute inset-0 rounded-full blur-xl", isDark ? "bg-[#FDF0EC]0/20" : "bg-amber-200/50")} />
        <div className={cn(
          "relative p-5 rounded-2xl border",
          isDark 
            ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-coral/20" 
            : "bg-[#FDF8F2] border-[#F5E9DE]"
        )}>
          <Target className={cn("w-14 h-14", isDark ? "text-coral" : "text-coral")} />
        </div>
      </div>
      <h3 className={cn("text-2xl font-bold mb-2", isDark ? "text-white" : "text-[#1A1A1A]")}>Start Your Journey</h3>
      <p className={cn("text-sm text-center max-w-md mb-8", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
        Create your first challenge and begin building unstoppable discipline.
      </p>
      <Button 
        type="button"
        onClick={onCreateGoal} 
        className="btn-glossy px-8 py-3 text-base"
        data-testid="empty-state-create-goal-btn"
      >
        <Plus className="w-5 h-5 mr-2" />
        Create First Challenge
      </Button>
    </div>
  );
}

// ============================================
// GOAL CARD - PREMIUM REDESIGN
// ============================================
function GoalCard({ goal, onLog, onStop, onPause, onResume, onUpdate, isDark }) {
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({ title: goal.title, target_count: goal.target_count });

  const isPaused = goal.status === 'PAUSED';
  const trackingType = goal.tracking_type || 'binary';
  
  // Calculate progress
  const dailyLogs = goal.daily_logs || [];
  let currentProgress = 0;
  if (trackingType === 'count') {
    currentProgress = dailyLogs.reduce((sum, log) => sum + (log.value || 0), 0);
  } else {
    const uniqueDays = new Set(dailyLogs.filter(l => l.value > 0).map(l => l.date));
    currentProgress = uniqueDays.size;
  }
  
  const progressPercent = Math.min(100, (currentProgress / goal.target_count) * 100);
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayLog = dailyLogs.find(l => l.date === todayStr);
  const isLoggedToday = todayLog && todayLog.value > 0;

  const handleQuickLog = () => {
    if (trackingType === 'binary') {
      onLog(isLoggedToday ? 0 : 1);
    } else {
      onLog((todayLog?.value || 0) + 1);
    }
  };

  // Progress color logic
  const getProgressColor = () => {
    if (progressPercent >= 100) return "bg-[#1A1A1A]";
    if (progressPercent >= 70) return "bg-coral";
    return "bg-coral";
  };

  return (
    <>
      <div 
        className={cn(
          "group relative p-5 rounded-2xl border transition-all duration-300 overflow-hidden",
          isDark 
            ? "bg-zinc-900/50 backdrop-blur-xl" 
            : "bg-white shadow-sm",
          isPaused 
            ? isDark ? "border-zinc-700/50 opacity-60" : "border-[#EEEAE0] opacity-60"
            : isLoggedToday 
              ? isDark ? "border-emerald-500/30 hover:border-emerald-500/50 shadow-lg shadow-emerald-500/5" : "border-emerald-300 hover:border-emerald-400 shadow-lg shadow-sm"
              : isDark ? "border-white/[0.06] hover:border-white/10" : "border-violet-50 hover:border-[#EEEAE0] hover:shadow-md"
        )}
        data-testid={`goal-card-${goal.id}`}
      >
        {/* Hover glow */}
        {!isPaused && (
          <div className={cn(
            "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-2xl",
            isLoggedToday ? "bg-emerald-500/10" : "bg-[#F4F3EE]0/10"
          )} />
        )}
        
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 pr-3">
            <h4 className={cn("font-semibold text-base truncate mb-1", isDark ? "text-white" : "text-[#1A1A1A]")}>{goal.title}</h4>
            <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
              <span className="font-mono">{currentProgress}/{goal.target_count}</span>
              <span className={isDark ? "text-zinc-600" : "text-[#9E8778]"}>•</span>
              <span>{goal.target_unit || 'times'}</span>
            </div>
          </div>
          
          {/* Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={cn(
                  "h-8 w-8 opacity-0 group-hover:opacity-100 transition-all",
                  isDark ? "text-zinc-500 hover:text-white" : "text-[#9E8778] hover:text-[#5F5F5F]"
                )}
                data-testid={`goal-menu-${goal.id}`}
              >
                <Settings className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#EEEAE0]"}>
              <DropdownMenuItem onClick={() => setShowEdit(true)} className={isDark ? "text-zinc-300 hover:text-white focus:text-white" : "text-[#5F5F5F] hover:text-[#1A1A1A]"}>
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </DropdownMenuItem>
              {isPaused ? (
                <DropdownMenuItem onClick={onResume} className={isDark ? "text-zinc-300 hover:text-white focus:text-white" : "text-[#5F5F5F] hover:text-[#1A1A1A]"}>
                  <Play className="w-4 h-4 mr-2" /> Resume
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={onPause} className={isDark ? "text-zinc-300 hover:text-white focus:text-white" : "text-[#5F5F5F] hover:text-[#1A1A1A]"}>
                  <Pause className="w-4 h-4 mr-2" /> Pause
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onStop} className="text-red-500 hover:text-red-400 focus:text-red-400">
                <StopCircle className="w-4 h-4 mr-2" /> Stop Goal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className={cn("flex justify-between text-xs mb-2", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
            <span>Progress</span>
            <span className={cn("font-semibold", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>{progressPercent.toFixed(0)}%</span>
          </div>
          <div className={cn("h-2.5 rounded-full overflow-hidden", isDark ? "bg-zinc-800/80" : "bg-white")}>
            <div 
              className={cn(
                "h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden bg-gradient-to-r",
                getProgressColor()
              )}
              style={{ width: `${progressPercent}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_ease-out]" />
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className={cn("flex items-center gap-2 text-xs", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
            <Clock className="w-3.5 h-3.5" />
            <span>Due {format(new Date(goal.end_date), 'MMM d, yyyy')}</span>
          </div>
          
          {!isPaused && (
            <Button
              size="sm"
              onClick={handleQuickLog}
              className={cn(
                "h-9 px-4 text-xs font-semibold transition-all",
                isLoggedToday 
                  ? "bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/30 border border-emerald-500/30" 
                  : "btn-glossy"
              )}
              data-testid={`goal-log-btn-${goal.id}`}
            >
              {isLoggedToday ? (
                <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Done</>
              ) : (
                <><Plus className="w-4 h-4 mr-1.5" /> Log</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#EEEAE0]"}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : "text-[#1A1A1A]"}>Edit Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={isDark ? "text-zinc-300" : "text-[#5F5F5F]"}>Title</Label>
              <Input 
                value={editData.title} 
                onChange={e => setEditData({...editData, title: e.target.value})}
                className={cn("mt-1.5", isDark ? "bg-zinc-800 border-zinc-700 text-white" : "bg-white border-[#E5E2DA] text-[#1A1A1A]")}
              />
            </div>
            <div>
              <Label className={isDark ? "text-zinc-300" : "text-[#5F5F5F]"}>Target</Label>
              <Input 
                type="number"
                value={editData.target_count} 
                onChange={e => setEditData({...editData, target_count: parseInt(e.target.value) || 1})}
                className={cn("mt-1.5", isDark ? "bg-zinc-800 border-zinc-700 text-white" : "bg-white border-[#E5E2DA] text-[#1A1A1A]")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)} className={isDark ? "border-zinc-700 text-zinc-300" : "border-[#E5E2DA] text-[#5F5F5F]"}>Cancel</Button>
            <Button onClick={() => { onUpdate(editData); setShowEdit(false); }} className="btn-glossy">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================
// PAST CHALLENGE CARD
// ============================================
function PastGoalCard({ goal, onRestart, isDark }) {
  const statusConfig = {
    COMPLETED: { icon: Trophy, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', border: isDark ? 'border-emerald-500/20' : 'border-[#EEEAE0]', label: 'Won' },
    FAILED: { icon: X, color: 'text-red-500', bg: isDark ? 'bg-red-500/10' : 'bg-red-50', border: isDark ? 'border-red-500/20' : 'border-red-200', label: 'Lost' },
    STOPPED: { icon: Ban, color: isDark ? 'text-zinc-400' : 'text-[#9E8778]', bg: isDark ? 'bg-zinc-500/10' : 'bg-white', border: isDark ? 'border-zinc-500/20' : 'border-[#EEEAE0]', label: 'Stopped' }
  };
  
  const config = statusConfig[goal.status] || statusConfig.STOPPED;
  const Icon = config.icon;
  
  return (
    <div className={cn(
      "flex items-center justify-between p-4 rounded-xl border transition-all duration-300",
      isDark ? "bg-zinc-900/40 hover:bg-zinc-800/50" : "bg-white hover:bg-white shadow-sm",
      config.border
    )}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={cn("p-2 rounded-lg", config.bg)}>
          <Icon className={cn("w-4 h-4", config.color)} />
        </div>
        <div className="min-w-0">
          <p className={cn("text-sm font-medium truncate", isDark ? "text-white" : "text-[#1A1A1A]")}>{goal.title}</p>
          <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#9E8778]")}>{config.label} • {format(new Date(goal.end_date), 'MMM d')}</p>
        </div>
      </div>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => onRestart(goal.id)} 
        className={cn(
          "h-8 px-3",
          isDark ? "text-zinc-500 hover:text-white hover:bg-white/5" : "text-[#9E8778] hover:text-[#5F5F5F] hover:bg-white"
        )}
      >
        <RotateCcw className="w-4 h-4" />
      </Button>
    </div>
  );
}

// ============================================
// MAIN DASHBOARD
// ============================================
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [goals, setGoals] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [sharedGoals, setSharedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [streak, setStreak] = useState(null);
  const [celebrateGoal, setCelebrateGoal] = useState(null);

  useEffect(() => {
    if (searchParams.get('subscription') === 'success') {
      toast.success("Welcome to Monk Mode Pro!");
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [goalsRes, groupsRes, statsRes, sharedRes, streakRes] = await Promise.all([
        api.get('/api/goals'),
        api.get('/api/groups'),
        api.get('/api/stats'),
        api.get('/api/shared-goals').catch(() => ({ data: [] })),
        api.get('/api/streak', { params: { today: format(new Date(), 'yyyy-MM-dd') } }).catch(() => ({ data: null }))
      ]);
      setStreak(streakRes.data);
      setGoals(goalsRes.data);
      setGroups(groupsRes.data);
      setUserStats(statsRes.data);
      setSharedGoals(sharedRes.data.filter(g => g.status === 'ACTIVE'));

      const expanded = {};
      groupsRes.data.forEach(g => expanded[g.id] = true);
      setExpandedGroups(expanded);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLog = async (goal, value) => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    try {
      const res = await api.post(`/api/goals/${goal.id}/log`, { value, date: todayStr });
      if (res.data.just_completed) {
        setCelebrateGoal(goal);
      } else {
        toast.success(value > 0 ? "+10 XP" : "Log removed");
      }
      fetchData();
    } catch (e) {
      toast.error("Failed to log progress");
    }
  };

  const handleStop = async (goalId) => {
    try {
      await api.patch(`/api/goals/${goalId}`, { status: 'STOPPED' });
      toast.success("Goal stopped");
      fetchData();
    } catch (e) {
      toast.error("Failed to stop goal");
    }
  };

  const handlePause = async (goalId) => {
    try {
      await api.patch(`/api/goals/${goalId}`, { status: 'PAUSED' });
      toast.success("Goal paused");
      fetchData();
    } catch (e) {
      toast.error("Failed to pause goal");
    }
  };

  const handleResume = async (goalId) => {
    try {
      await api.patch(`/api/goals/${goalId}`, { status: 'ACTIVE' });
      toast.success("Goal resumed");
      fetchData();
    } catch (e) {
      toast.error("Failed to resume goal");
    }
  };

  const handleRestart = async (goalId) => {
    try {
      await api.post(`/api/goals/${goalId}/restart`);
      toast.success("Goal restarted!");
      fetchData();
    } catch (e) {
      toast.error("Failed to restart goal");
    }
  };

  const handleUpdate = async (goalId, data) => {
    try {
      await api.patch(`/api/goals/${goalId}`, data);
      toast.success("Goal Updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update goal");
    }
  };

  const handleSharedGoalLog = async (goalId) => {
    try {
      const res = await api.put(`/api/shared-goals/${goalId}/progress`, {
        progress_delta: 1
      });
      
      toast.success(`+1 logged! (${res.data.current_progress}/${res.data.target_value})`);
      
      if (res.data.status === 'COMPLETED') {
        confetti({ particleCount: 100, spread: 60, origin: { y: 0.6 } });
        toast.success('Shared goal completed! 🎉');
      }
      
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log progress");
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Computed stats
  const stats = useMemo(() => {
    const activeGoals = goals.filter(g => g.status === 'ACTIVE');
    const completedGoals = goals.filter(g => g.status === 'COMPLETED');
    return { activeGoals: activeGoals.length, completedGoals: completedGoals.length, streak: streak?.current_streak || 0 };
  }, [goals, streak]);

  // Group goals
  const groupedGoals = useMemo(() => {
    const grouped = {};
    groups.forEach(g => grouped[g.id] = []);
    goals.filter(g => g.status === 'ACTIVE' || g.status === 'PAUSED').forEach(goal => {
      if (grouped[goal.group_id]) {
        grouped[goal.group_id].push(goal);
      }
    });
    return grouped;
  }, [goals, groups]);

  const inactiveGoals = goals.filter(g => g.status === 'COMPLETED' || g.status === 'FAILED' || g.status === 'STOPPED');
  const hasActiveGoals = goals.some(g => g.status === 'ACTIVE' || g.status === 'PAUSED');

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <div className="min-h-screen bg-[#FAF9F5]" data-testid="dashboard-page">
      <Navbar />

      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* ===== HEADER: greeting + mascot ===== */}
        <div className="flex items-start justify-between gap-6 mb-10 sm:mb-14">
          <div className="min-w-0 flex-1">
            <p className="sl-section-tag mb-3">{format(new Date(), 'EEEE, MMM d')}</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-[1.05]">
              {greeting}.<br />
              <span className="text-[#9E8778] font-medium">Keep your word.</span>
            </h1>
            <p className="mt-4 text-[#5F5F5F] text-sm sm:text-base max-w-md">
              {hasActiveGoals
                ? `${stats.activeGoals} active ${stats.activeGoals === 1 ? 'commitment' : 'commitments'}. ${stats.streak > 0 ? `${stats.streak}-day streak.` : "Let's begin the day."}`
                : 'A quiet morning. Set your first commitment.'}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <Button
                onClick={() => navigate('/create-goal')}
                className="sl-pill-dark h-auto"
                data-testid="new-challenge-btn"
              >
                <Plus className="w-4 h-4" /> New commitment
              </Button>
              {hasActiveGoals && (
                <Button
                  onClick={() => navigate('/weekly')}
                  variant="ghost"
                  className="sl-pill-ghost h-auto"
                >
                  Weekly view <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Floating monk mascot + streak */}
          <div className="hidden sm:flex flex-shrink-0 items-center gap-6">
            <div className="w-32 h-32 lg:w-40 lg:h-40 monk-float">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/75765fb2-f934-4a78-b9d4-83c5dd8dbc83/images/af05521cdbf5dee58e32af61f09c6d9408039100be1a7125afbc92f0d3489591.jpeg"
                alt=""
                className="w-full h-full object-contain monk-breathe rounded-2xl"
              />
            </div>
            <StreakWidget streak={streak} />
          </div>
        </div>
        <div className="sm:hidden -mt-4 mb-8">
          <StreakWidget streak={streak} />
        </div>

        {/* ===== STATS ROW — compact inline ===== */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          <SimpleStat label="Active" value={stats.activeGoals} />
          <SimpleStat label="Shared" value={sharedGoals.length} />
          <SimpleStat label="Completed" value={stats.completedGoals} />
          <SimpleStat label="XP" value={userStats?.xp || 0} />
        </div>

        {/* ===== SHARED CHALLENGES STRIP ===== */}
        {sharedGoals.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-baseline gap-3">
                <h3 className="text-base font-semibold text-[#1A1A1A]">Shared challenges</h3>
                <span className="font-mono text-xs text-[#9E8778]">{sharedGoals.length}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/shared-goals')}
                className="text-sm text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-transparent"
              >
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
              {sharedGoals.slice(0, 4).map(goal => (
                <SharedGoalCard
                  key={goal.id}
                  goal={goal}
                  onLog={() => handleSharedGoalLog(goal.id)}
                  onNavigate={() => navigate(`/shared-goals/${goal.id}`)}
                  isDark={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* ===== ACTIVE CHALLENGES ===== */}
        <section className="mb-12">
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] tracking-tight">Your commitments</h2>
            <span className="font-mono text-xs text-[#9E8778]">
              {goals.filter(g => g.status === 'ACTIVE' || g.status === 'PAUSED').length}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 rounded-full border-2 border-[#EEEAE0] border-t-[#1A1A1A] animate-spin" />
            </div>
          ) : !hasActiveGoals ? (
            <EmptyState onCreateGoal={() => navigate('/create-goal')} isDark={false} />
          ) : (
            <div className="space-y-8">
              {groups.map(group => {
                const groupGoals = groupedGoals[group.id] || [];
                if (groupGoals.length === 0) return null;

                return (
                  <div key={group.id}>
                    <button
                      className="flex items-center gap-2 mb-4 group"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform duration-200 text-[#9E8778]",
                        !expandedGroups[group.id] && "-rotate-90"
                      )} />
                      <FolderOpen className="w-4 h-4 text-[#9E8778]" />
                      <span className="font-medium text-sm text-[#5F5F5F] group-hover:text-[#1A1A1A] transition-colors">
                        {group.name}
                      </span>
                      <span className="font-mono text-xs text-[#9E8778]">{groupGoals.length}</span>
                    </button>

                    {expandedGroups[group.id] && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pl-6">
                        {groupGoals.map(goal => (
                          <GoalCard
                            key={goal.id}
                            goal={goal}
                            onLog={(val) => handleLog(goal, val)}
                            onStop={() => handleStop(goal.id)}
                            onPause={() => handlePause(goal.id)}
                            onResume={() => handleResume(goal.id)}
                            onUpdate={(data) => handleUpdate(goal.id, data)}
                            isDark={false}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ===== PAST CHALLENGES ===== */}
        {inactiveGoals.length > 0 && (
          <section>
            <div className="flex items-baseline gap-3 mb-5">
              <h2 className="text-base font-medium text-[#5F5F5F] tracking-tight">Past commitments</h2>
              <span className="font-mono text-xs text-[#9E8778]">
                {goals.filter(g => g.status === 'COMPLETED').length} won · {goals.filter(g => g.status === 'FAILED').length} lost
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inactiveGoals.slice(0, 6).map(goal => (
                <PastGoalCard key={goal.id} goal={goal} onRestart={handleRestart} isDark={false} />
              ))}
            </div>
            {inactiveGoals.length > 6 && (
              <div className="mt-6 text-center">
                <Button variant="ghost" onClick={() => navigate('/history')} className="text-sm text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-transparent">
                  View all history <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
      <GoalCelebration goal={celebrateGoal} onClose={() => setCelebrateGoal(null)} />
    </div>
  );
}

/* ============================================
   SIMPLE STAT - compact inline stat
   ============================================ */
function SimpleStat({ label, value }) {
  return (
    <div className="sl-card px-4 py-4">
      <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1A1A1A] leading-none">
        {value}
      </div>
      <div className="text-xs text-[#9E8778] mt-1.5 tracking-wider uppercase font-mono">
        {label}
      </div>
    </div>
  );
}
